import React from "react";
import { Box, TextField, useTheme, Select, MenuItem, FormControl, InputLabel, Autocomplete, Typography } from "@mui/material";
import { Formik, Form } from "formik";
import * as yup from "yup";
import { DataGrid, GridActionsCellItem } from "@mui/x-data-grid";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import AgricultureOutlined from "@mui/icons-material/AgricultureOutlined";
import { useCreateForceMutation, useDeleteForceMutation } from "../services/forceService";
import { tokens } from "../theme";
import { dataGridStyles, localeText } from "../utils/dataGridConfig";
import { useGetTemplatesQuery } from "../services/templateService";
import { formatDate } from "../utils/formatDate";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import SuccessButton from "./SuccessButton";
import { useAlert } from "../global/AlertContext";
import dayjs from 'dayjs'; // Import dayjs
import { useNavigate } from "react-router-dom";

const ForcesList = ({ refetchLote, lote, isReadOnly = false }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const showAlert = useAlert();
  const navigate = useNavigate();

  // Mutation hooks for creating and deleting forces
  const [createForce] = useCreateForceMutation();
  const [deleteForce] = useDeleteForceMutation();

  // Query to fetch templates
  const { data: templates, error: templatesError, isLoading: templatesLoading } = useGetTemplatesQuery({},{
    refetchOnMountOrArgChange: true,
  });

  // Query to fetch forces by lote ID
  const forces = lote?.forces;

  // Validation schema for the force form
  const forceSchema = yup.object().shape({
    forced_date: yup
      .date()
      .nullable()
      .typeError("Ingrese una fecha válida") // Custom error message
      .required("Requerido"),
    template_id: yup.string().required("Requerido"),
    blocks: yup
      .array()
      .min(1, "Debe seleccionar al menos un bloque")
      .test("blocks", "No puede seleccionar bloques ya forzados", (value) => {
        return value.every((blockId) => {
          console.log(blockId)
          const block = lote?.baseBlocks?.find((b) => b.id === blockId);
          console.log(block)
          return !(block === undefined); // Ensure the block is not part of a force
        });
      })
      .required("Requerido"),
  });

  // Function to handle the creation of a force
  const handleCreateForce = async (values, { resetForm }) => {
    try {
      const payload = {
        forced_date: values.forced_date ? values.forced_date.format('YYYY-MM-DD') : null,
        template_id: values.template_id,
        lote_id: lote.id,
        blocks: values.blocks,
      };
      await createForce(payload).unwrap();
      refetchLote();
      resetForm();
      showAlert("Forza Creada", "success");
    } catch (error) {
      showAlert(error.message, "error");
    }
  };

  // Function to handle the deletion of a force
  const handleDeleteForce = async (id) => {
    try {
      await deleteForce(id).unwrap();
      refetchLote();
      showAlert("Forza Borrada", "success");
    } catch (error) {
      showAlert(error.message, "error");
    }
  };

  // Function to handle the view action
  const handleSeeApplications = (id) => {    
    navigate(`/applications?force_id=${id}?lote_id=${lote.id}`, '_blank', 'noopener,noreferrer');
  };

  // Define columns for the DataGrid
  const columns = [
    {
      field: "number",
      headerName: "Número",
      flex: 0.7,
      cellClassName: "name-column--cell",
    },
    {
      field: "forced_week",
      headerName: "Sem Forza",
      headerAlign: "left",
      align: "left",
      flex:  0.5,
    },
    {
      field: "forced_date",
      headerName: "Fecha Forza",
      flex: 0.5,
      valueFormatter: (value) => formatDate(value),
    },
    {
      field: "harvest_week",
      headerName: "Sem Cosecha",
      headerAlign: "left",
      align: "left",
      flex:  0.5,
    },
    {
      field: "harvest_date",
      headerName: "Fecha Cosecha",
      flex: 0.5,
      valueFormatter: (value) => formatDate(value),
    },
    {
      field: "area",
      headerName: "Area",
      flex: 0.4,
    },
    {
      field: "plants",
      headerName: "Plantas",
      flex: 0.4,
    },
  ];

  // Add the "Actions" column only if isReadOnly is false
  if (!isReadOnly) {
    columns.push({
      field: "actions",
      type: "actions",
      headerName: "Acciones",
      flex: 1,
      getActions: (params) => [
        <GridActionsCellItem
          icon={<AgricultureOutlined />}
          label="View"
          onClick={() => handleSeeApplications(params.id)}
        />,
        <GridActionsCellItem
          icon={<DeleteOutlinedIcon />}
          label="Delete"
          onClick={() => handleDeleteForce(params.id)}
        />,
      ],
    });
  }

  return (
    <Box>
      {/* Form to create a new force */}
      {!isReadOnly && (
        <Formik
          initialValues={{ forced_date: null, template_id: "", blocks: [] }}
          validationSchema={forceSchema}
          onSubmit={handleCreateForce}
        >
          {({ values, errors, touched, handleBlur, handleChange, handleSubmit, setFieldValue }) => (
            <Form onSubmit={handleSubmit}>
              <Box 
                display="grid"
                gap="10px"
                alignItems="center"
                gridTemplateColumns="repeat(20, minmax(0, 1fr))"
              >
                {/* Template Selection */}
                <FormControl fullWidth variant="filled" sx={{ gridColumn: "span 4" }}>
                  <Autocomplete
                    options={templates?.records || []}
                    getOptionLabel={(option) => option.name}
                    value={templates?.records.find((template) => template.id === values.template_id) || null}
                    onChange={(event, newValue) => {
                      setFieldValue("template_id", newValue ? newValue.id : "");
                    }}
                    onBlur={handleBlur}
                    loading={templatesLoading}
                    noOptionsText={templatesError ? "Error al cargar plan" : "No se encontraron planes"}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Plan de aplicaciones"
                        variant="filled"
                        error={!!touched.template_id && !!errors.template_id}
                        helperText={touched.template_id && errors.template_id}
                      />
                    )}
                  />
                </FormControl>

                {/* Date Selection */}
                <Box  sx={{ gridColumn: "span 4" }}>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                      label="Fecha de Forzado"
                      value={values.forced_date ? dayjs(values.forced_date) : null} // Convert to Day.js object
                      onChange={(newValue) => setFieldValue("forced_date", newValue)} // Set as Day.js object
                      format="DD/MM/YYYY" // Set user-friendly format
                      slotProps={{
                        textField: {
                          variant: "filled",
                          fullWidth: true,
                          error: !!touched.forced_date && !!errors.forced_date,
                          helperText: touched.forced_date && errors.forced_date,
                          sx: { flex: 1 }
                        }
                      }}
                    />
                  </LocalizationProvider>
                </Box>

                {/* Block Selection */}
                <FormControl fullWidth variant="filled" sx={{ gridColumn: "span 10" }}>
                  <InputLabel id="blocks-label">Seleccionar Bloques</InputLabel>
                  <Select
                    labelId="blocks-label"
                    multiple
                    value={values.blocks}
                    onChange={(e) => setFieldValue("blocks", e.target.value)}
                    renderValue={(selected) => selected.map(id => lote.blocks.find(block => block.id === id).number).join(', ')}
                    error={!!touched.blocks && !!errors.blocks}
                  >
                    {lote?.baseBlocks?.map((block) => (
                      <MenuItem key={block.id} value={block.id}>
                        {block.number}
                      </MenuItem>
                    ))}
                  </Select>
                  {touched.blocks && errors.blocks && (
                    <Typography variant="caption" color="error">
                      {errors.blocks}
                    </Typography>
                  )}
                </FormControl>

                {/* Submit Button */}
                <Box
                  sx={{ gridColumn: "span 2" }}
                  textAlign="center"
                >
                  <SuccessButton label="Crear Forza" />
                </Box>
                
              </Box>
            </Form>
          )}
        </Formik>
      )}

      {/* DataGrid to display forces */}
      <Box height="50vh" sx={dataGridStyles(colors)}>
        <DataGrid
          rows={forces || []}
          columns={columns}
          getRowId={(row) => row.group_id}
          pageSizeOptions={[100]}
          disableRowSelectionOnClick
          disableColumnMenu
          disableColumnSorting
          localeText={localeText}
        />
      </Box>
    </Box>
  );
};

export default ForcesList;